import {
  Close as CloseIcon,
  AutoAwesome as TriageSparkleIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Archive as ArchiveIcon,
  Replay as ReplayIcon,
  Email as EmailIcon,
  CheckCircleOutline as ReviewedIcon,
  InsertDriveFileOutlined as FileIcon,
  Check as CheckIcon,
  Clear as XIcon,
  Add as PlusIcon,
  Language as WebIcon,
  SearchOff as MissingIcon,
  KeyboardArrowUp as ChevronUpIcon,
  KeyboardArrowDown as ChevronDownIcon,
} from "@mui/icons-material";
import {
  Box,
  Button as MuiButton,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import * as logger from "common/logger";
import { dpString, parseNumberString } from "common/numbers";
import Button from "components/common/Button";
import SourcedValue from "components/common/SourcedValue";
import {
  fieldDisplayName,
  isInScope,
  LAYER_FIELDS,
  operatorLabel,
  ruleRequirementsByName,
} from "components/triagePipeline/triageDefinitions";
import { triggerBlobDownload } from "fileUtils";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useSaveTriageOverridesMutation } from "services/aiAgents";
import { useFileUrlQuery } from "services/filesService";
import { EMAIL_BODY_FILENAMES } from "utils/ingestionFiles";

// Clickable source file link — fetches presigned download URL on demand.
// Falls back to directUrl (from attachment_files) when file_id is unavailable.
// Email body sources render as an inline email icon button instead of a filename.
const SourceLink = ({
  filename,
  page_number,
  file_id,
  isEmailBody,
  onOpenEmail,
  directUrl,
}) => {
  const { data: urlData } = useFileUrlQuery(file_id, {
    skip: !file_id || isEmailBody,
  });
  const label = page_number ? `${filename} · p.${page_number}` : filename;

  const resolvedUrl = urlData?.url || directUrl || null;

  const handleDownload = useCallback(
    (e) => {
      if (!resolvedUrl) return;
      e.preventDefault();
      triggerBlobDownload(resolvedUrl, filename);
    },
    [resolvedUrl, filename]
  );

  // Email body: render like a file link but with the email icon and label "Email"
  if (isEmailBody) {
    return (
      <Stack direction={"row"} alignItems={"center"} spacing={0.75}>
        <EmailIcon
          sx={{ fontSize: "0.7rem", color: "text.disabled", flexShrink: 0 }}
        />
        <Typography
          component={"span"}
          onClick={onOpenEmail}
          sx={{
            fontSize: 11,
            fontFamily: "monospace",
            color: "text.secondary",
            borderBottom: "1px dashed",
            borderColor: "divider",
            cursor: "pointer",
            "&:hover": { color: "text.primary", borderColor: "text.secondary" },
          }}
        >
          {"Email"}
        </Typography>
      </Stack>
    );
  }

  const linkSx = {
    fontSize: 11,
    fontFamily: "monospace",
    color: "text.secondary",
    textDecoration: "none",
    borderBottom: "1px dashed",
    borderColor: "divider",
    cursor: "pointer",
    "&:hover": { color: "text.primary", borderColor: "text.secondary" },
  };

  return (
    <Stack direction={"row"} alignItems={"center"} spacing={0.75}>
      <FileIcon
        sx={{ fontSize: "0.7rem", color: "text.disabled", flexShrink: 0 }}
      />
      {resolvedUrl ? (
        <Typography
          component={"a"}
          href={resolvedUrl}
          onClick={handleDownload}
          sx={linkSx}
        >
          {label}
        </Typography>
      ) : (
        <Typography
          sx={{ fontSize: 11, fontFamily: "monospace", color: "text.disabled" }}
        >
          {label}
        </Typography>
      )}
    </Stack>
  );
};

// The pipeline row shows the same fact as a dot; both carry this wording. Deliberately
// says "changed" rather than "added": REMOVING a document marks the report out of date too,
// and the old wording told a reviewer who had just detached something that files had been
// added. Where the number of unread documents is known, the more specific hint below is
// used instead.
export const STALE_REPORT_HINT =
  "The documents have changed since this report ran — re-run triage to bring it up to date";

// Short on purpose. The earlier wording listed every removed filename inside the tooltip,
// which turned a prompt into a paragraph; the names live in the item's membership history.
export const removedDocumentsHint = (count) =>
  `${count} document${count === 1 ? " was" : "s were"} removed. ` +
  "Re-run to bring this report up to date.";

const NEW_DOCUMENTS_HINT =
  "Files were added after this report ran — re-run triage to cover the full set";

const OUTCOME_META = {
  pass: { color: "success", Icon: CheckIcon, label: "Pass" },
  fail: { color: "error", Icon: XIcon, label: "Fail" },
  missing: { color: "warning", Icon: MissingIcon, label: "Missing" },
  uncertain: { color: "default", Icon: MissingIcon, label: "Uncertain" },
};

// A value tagged with where it came from. When the documents and the web disagree the row
// shows both, one per line, so the reader compares them directly instead of reading a
// governing value here and its rival in a sentence underneath.
const formatExtractedValue = (val) => {
  if (val === null || val === undefined || val === "") return null;
  const numeric = parseNumberString(String(val));
  return numeric == null ? String(val) : dpString(2, { minDp: 0 })(numeric);
};

// How a combined rule actually evaluated, one condition per line.
//
// The row used to carry only the sentence "Combined rule 'Test': (insured AND insured) →
// pass", which restates the outcome pill beside it and leaves the reader no way to see
// which condition decided the verdict, or on what value. Each leaf shows the field, what it
// required, the value it was judged on and its own outcome; a nested bracket indents.
const JOINERS = {
  and: "AND",
  or: "OR",
  and_not: "AND NOT",
  or_not: "OR NOT",
};

// The joiner before a child: the _not variants negate every child after the first.
const joinerFor = (combinator, index) => {
  if (index === 0) return null;
  const joiner = JOINERS[combinator] || "AND";
  return combinator === "and_not" || combinator === "or_not"
    ? joiner
    : joiner.split(" ")[0];
};

const ConditionOutcome = ({ outcome }) => {
  const meta = OUTCOME_META[outcome] || OUTCOME_META.uncertain;
  const color = meta.color === "default" ? null : meta.color;
  return (
    <Typography
      component={"span"}
      sx={{
        fontSize: 11,
        fontWeight: 500,
        color: color ? `${color}.dark` : "text.secondary",
        // A fixed, right-aligned column so every verdict in the tree lines up under the
        // one above it, however deep its row sits.
        width: 58,
        textAlign: "right",
        flexShrink: 0,
      }}
    >
      {meta.label}
    </Typography>
  );
};

// A bracket's name in words, so a reader does not have to know what "and_not" means.
const GROUP_INTENT = {
  and: "ALL OF",
  or: "ANY OF",
  and_not: "FIRST, BUT NONE OF",
  or_not: "FIRST, OR NONE OF",
};

const JOINER_WIDTH = 52;

// One condition or one bracket. Every row puts its outcome in the same right-hand column
// and its joiner in the same left one, so a reader scans the verdicts down a single line
// instead of hunting for them at the end of ragged text. A bracket draws a rail down its
// children, which is what makes the nesting readable at a glance.
// The expression flattened to rows, each carrying how deep it sits. A nested component
// tree put every level's outcome inside its own indented box, so the verdicts stepped
// inward with the nesting and could not be read down a column. One flat list means one
// outcome column, whatever the depth.
const flattenExpression = (node, combinator, index, depth, rows) => {
  rows.push({ node, joiner: joinerFor(combinator, index), depth });
  (node.children || []).forEach((child, i) =>
    flattenExpression(child, node.combinator, i, depth + 1, rows)
  );
  return rows;
};

const ExpressionRow = ({ node, joiner, depth }) => {
  const isBracket = Boolean(node.children);
  return (
    <Stack direction={"row"} alignItems={"baseline"} spacing={0.75}>
      <Typography
        component={"span"}
        sx={{
          fontSize: 10,
          fontWeight: 600,
          color: "text.disabled",
          width: JOINER_WIDTH,
          flexShrink: 0,
          textAlign: "right",
        }}
      >
        {joiner || ""}
      </Typography>
      <Stack
        direction={"row"}
        alignItems={"baseline"}
        spacing={0.75}
        flexWrap={"wrap"}
        sx={(theme) => ({
          flex: 1,
          minWidth: 0,
          // The rail: consecutive rows at one depth draw a continuous line down the
          // children of the bracket that holds them.
          ml: `${depth * 14}px`,
          pl: depth > 0 ? 1 : 0,
          borderLeft:
            depth > 0 ? `2px solid ${theme.palette.divider}` : undefined,
        })}
      >
        {isBracket ? (
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.06em",
              color: "text.disabled",
            }}
          >
            {[node.label, GROUP_INTENT[node.combinator] || "ALL OF"]
              .filter(Boolean)
              .join(" · ")
              .toUpperCase()}
          </Typography>
        ) : (
          <>
            <Typography
              sx={{ fontSize: 12, color: "text.primary", fontWeight: 500 }}
            >
              {fieldDisplayName(node.field_name)}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: "text.disabled" }}>
              {[operatorLabel(node.operator), formatExtractedValue(node.value)]
                .filter(Boolean)
                .join(" ")}
            </Typography>
            <Typography
              sx={{
                fontSize: 11.5,
                color: "text.secondary",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {node.extracted_value == null
                ? "— nothing extracted"
                : `— ${formatExtractedValue(node.extracted_value)}`}
            </Typography>
            {node.judged_in_code && (
              <Tooltip
                title={
                  "Checked here against the extracted value. The model is asked one condition per field, so a second condition on the same field is applied in code."
                }
                arrow
              >
                <Typography
                  sx={{ fontSize: 10, color: "text.disabled", cursor: "help" }}
                >
                  {"computed"}
                </Typography>
              </Tooltip>
            )}
          </>
        )}
      </Stack>
      <ConditionOutcome outcome={node.outcome} />
    </Stack>
  );
};

const RuleExpression = ({ expression }) => {
  if (!expression?.children?.length) return null;
  const rows = expression.children.reduce(
    (acc, child, i) =>
      flattenExpression(child, expression.combinator, i, 0, acc),
    []
  );
  return (
    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
      {rows.map((row, i) => (
        <ExpressionRow key={i} {...row} />
      ))}
    </Stack>
  );
};

// One page the web value was found on. The host carries the weight — a reader judges
// "sec.gov" at a glance and cannot read a full query string — with the whole URL on hover.
const WebSourceLink = ({ url }) => {
  let host = url;
  try {
    host = new URL(url).hostname.replace(/^www\./, "");
  } catch (e) {
    // Not a URL the browser can parse; show it as given rather than dropping the source.
  }
  // new URL("javascript:...") parses, so only http(s) sources become anchors.
  const linkProps = /^https?:/i.test(url)
    ? {
        component: "a",
        href: url,
        target: "_blank",
        rel: "noopener noreferrer",
      }
    : {};
  return (
    <Tooltip title={url} arrow>
      <Stack
        {...linkProps}
        direction={"row"}
        alignItems={"center"}
        spacing={0.5}
        sx={{
          textDecoration: "none",
          color: "text.secondary",
          width: "fit-content",
          "&:hover": { color: "primary.main" },
        }}
      >
        <WebIcon sx={{ fontSize: "0.8rem", color: "inherit" }} />
        <Typography
          sx={{
            fontSize: 11.5,
            color: "inherit",
            borderBottom: "1px dashed",
            borderColor: "divider",
          }}
        >
          {host}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

const RuleCard = ({
  r,
  override,
  editingField,
  onStartEdit,
  onCancelEdit,
  onDraftChange,
  onSaveDraft,
  draft,
  emailBodyFileId,
  onOpenEmail,
  attachmentFiles,
}) => {
  const effective = override || r;

  // Build filename → presigned URL map from attachment_files for source link fallback
  const attachmentUrlMap = useMemo(() => {
    if (!attachmentFiles?.length) return {};
    return Object.fromEntries(
      attachmentFiles.map((f) => [f.file_name, f.file_url])
    );
  }, [attachmentFiles]);
  const isEdited = Boolean(override);
  const isEditing = editingField === r.field_name;

  // When editing, derive display colors from the draft outcome so the card
  // updates reactively as the user changes the dropdown.
  const displayOutcomeKey = isEditing
    ? draft?.outcome ?? "uncertain"
    : effective.outcome ?? effective.user_outcome ?? "uncertain";
  const meta = OUTCOME_META[displayOutcomeKey] || OUTCOME_META.uncertain;
  const { Icon: OutcomeIcon } = meta;
  const webGoverned = !isEdited && r.value_source === "web" && r.web_value;
  const formattedValue = webGoverned
    ? formatExtractedValue(r.web_value)
    : formatExtractedValue(
        effective.extracted_value ?? effective.user_extracted_value
      );
  // The version that did not govern, shown under the row. Provenance describes the AI
  // evaluation, so an edited row hides it — the human override supersedes both sources.
  const alternateValue = isEdited
    ? null
    : webGoverned
    ? formatExtractedValue(r.extracted_value)
    : r.web_value
    ? formatExtractedValue(r.web_value)
    : null;
  const accentColor = meta.color === "default" ? null : meta.color;

  return (
    <Box
      sx={(theme) => ({
        p: "14px 16px",
        borderRadius: 2,
        border: `1px solid ${
          isEditing
            ? accentColor
              ? alpha(theme.palette[accentColor].main, 0.45)
              : theme.palette.divider
            : isEdited
            ? alpha(theme.palette.info.main, 0.35)
            : theme.palette.divider
        }`,
        bgcolor: isEditing
          ? accentColor
            ? alpha(theme.palette[accentColor].main, 0.03)
            : "background.paper"
          : isEdited
          ? alpha(theme.palette.info.main, 0.04)
          : "background.paper",
        "&:hover": !isEditing ? { borderColor: "action.disabled" } : {},
        transition: "border-color 80ms, background-color 80ms",
      })}
    >
      {/* Header row */}
      <Stack direction={"row"} spacing={1.25} alignItems={"flex-start"}>
        {/* Circular outcome icon — updates live while editing */}
        <Box
          sx={(theme) => ({
            width: 20,
            height: 20,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: "1px",
            bgcolor: accentColor
              ? alpha(theme.palette[accentColor].main, 0.12)
              : alpha(theme.palette.text.primary, 0.06),
          })}
        >
          <OutcomeIcon
            sx={(theme) => ({
              fontSize: "0.72rem",
              color: accentColor
                ? theme.palette[accentColor].main
                : theme.palette.text.secondary,
            })}
          />
        </Box>

        <Box flex={1} minWidth={0}>
          {/* Rule name + value tag + edited badge */}
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            flexWrap={"wrap"}
            mb={0.5}
          >
            <Typography
              variant={"body2"}
              fontWeight={600}
              sx={{ fontSize: "13.5px" }}
            >
              {fieldDisplayName(r.field_name)}
            </Typography>
            {/* Documents-only rows keep a plain tag: a source icon on every ordinary row
                would be noise. The icon appears once the web was involved. */}
            {formattedValue && !isEditing && !alternateValue && !webGoverned && (
              <Typography
                component={"span"}
                sx={(theme) => ({
                  px: "6px",
                  py: "1px",
                  borderRadius: "4px",
                  bgcolor: alpha(theme.palette.text.primary, 0.05),
                  border: `1px solid ${theme.palette.divider}`,
                  fontFamily: "monospace",
                  fontSize: "11.5px",
                  color: "text.primary",
                  fontWeight: 400,
                })}
              >
                {formattedValue}
              </Typography>
            )}
            {isEdited && !isEditing && (
              <Chip
                label={"Edited"}
                size={"small"}
                color={"info"}
                variant={"outlined"}
                sx={{ fontSize: "0.62rem", height: 18, fontWeight: 700 }}
              />
            )}
            {r.source_flagged && !isEdited && !isEditing && (
              <Chip
                label={"Sources disagree"}
                size={"small"}
                color={"warning"}
                variant={"outlined"}
                sx={{ fontSize: "0.62rem", height: 18, fontWeight: 700 }}
              />
            )}
            {r.confidence === "low" && !isEdited && !isEditing && (
              <Tooltip
                title={
                  "A document added later contradicted the earlier value. The reasoning says which source was believed."
                }
                arrow
              >
                <Chip
                  label={"Low confidence"}
                  size={"small"}
                  color={"warning"}
                  variant={"outlined"}
                  sx={{ fontSize: "0.62rem", height: 18, fontWeight: 700 }}
                />
              </Tooltip>
            )}
          </Stack>

          {/* What an incremental re-run superseded, so the change is visible not silent */}
          {r.prior_value != null && !isEditing && (
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              display={"block"}
              sx={{ fontSize: "0.68rem", fontStyle: "italic" }}
            >
              {`was: ${r.prior_value}`}
            </Typography>
          )}

          {/* Web-sourced with nothing to compare against — still say where it came from */}
          {!isEditing && !alternateValue && webGoverned && formattedValue && (
            <Box sx={{ mt: 0.25, mb: 0.5 }}>
              <SourcedValue source={"web"} value={formattedValue} governed />
            </Box>
          )}

          {/* Both versions, each tagged with its source, governing one first */}
          {!isEditing && alternateValue && (
            <Stack spacing={0.25} sx={{ mt: 0.25, mb: 0.5 }}>
              <SourcedValue
                source={webGoverned ? "web" : "submission"}
                value={formattedValue}
                governed
              />
              <SourcedValue
                source={webGoverned ? "submission" : "web"}
                value={alternateValue}
                governed={false}
              />
            </Stack>
          )}

          {/* How it evaluated: the expression for a combined rule, the model's reasoning
              otherwise. The stored sentence for a combined rule only restates the outcome
              pill beside it, so it stays on the record and off the screen. */}
          {!isEditing &&
            (r.expression && !isEdited ? (
              <RuleExpression expression={r.expression} />
            ) : (
              <Typography
                variant={"caption"}
                color={"text.secondary"}
                display={"block"}
                lineHeight={1.55}
                sx={{ fontSize: "12.5px" }}
              >
                {effective.reasoning ?? effective.user_reasoning}
              </Typography>
            ))}

          {/* AI original when edited */}
          {isEdited && !isEditing && (
            <Typography
              variant={"caption"}
              color={"text.disabled"}
              display={"block"}
              sx={{ mt: 0.4, fontStyle: "italic" }}
            >
              {`AI: ${
                OUTCOME_META[override.ai_outcome]?.label ?? override.ai_outcome
              }`}
              {override.ai_extracted_value
                ? ` · ${formatExtractedValue(override.ai_extracted_value)}`
                : ""}
            </Typography>
          )}

          {/* Source links */}
          {!isEditing && r.source_locations?.length > 0 && (
            <Stack spacing={0.5} mt={0.75}>
              {r.source_locations.map((s, i) => (
                <SourceLink
                  key={i}
                  filename={s.filename}
                  page_number={s.page_number}
                  file_id={s.file_id}
                  isEmailBody={Boolean(
                    (emailBodyFileId && s.file_id === emailBodyFileId) ||
                      EMAIL_BODY_FILENAMES.has(s.filename)
                  )}
                  onOpenEmail={onOpenEmail}
                  directUrl={attachmentUrlMap[s.filename] ?? null}
                />
              ))}
            </Stack>
          )}

          {/* Where the web value was found. A document value cites a file and page; a web
              value used to cite nothing, so the reader was asked to trust it. */}
          {!isEditing && !isEdited && r.web_source_urls?.length > 0 && (
            <Stack spacing={0.25} mt={0.75}>
              {r.web_source_urls.map((url, i) => (
                <WebSourceLink key={i} url={url} />
              ))}
            </Stack>
          )}
        </Box>

        {/* Right: pill + edit button (hidden while editing) */}
        {!isEditing && (
          <Stack
            direction={"row"}
            spacing={0.5}
            alignItems={"center"}
            flexShrink={0}
          >
            <Box
              sx={(theme) => ({
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                height: 20,
                px: "8px",
                borderRadius: "10px",
                fontSize: 11.5,
                fontWeight: 500,
                ...(accentColor === "success" && {
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: "success.dark",
                }),
                ...(accentColor === "error" && {
                  bgcolor: alpha(theme.palette.error.main, 0.12),
                  color: "error.dark",
                }),
                ...(accentColor === "warning" && {
                  bgcolor: alpha(theme.palette.warning.main, 0.12),
                  color: "warning.dark",
                }),
                ...(!accentColor && {
                  bgcolor: alpha(theme.palette.text.primary, 0.07),
                  color: "text.secondary",
                }),
              })}
            >
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  flexShrink: 0,
                  bgcolor: accentColor
                    ? `${accentColor}.main`
                    : "text.disabled",
                }}
              />
              {meta.label}
            </Box>
            {/* A layer rule's verdict is computed against the tower, so it is not a
                reviewer's to edit — and an override on one moved the headline score while
                every per-layer score and the verdict ignored it. */}
            {!LAYER_FIELDS.has(r.field_name) && (
              <Tooltip title={"Edit this rule result"}>
                <IconButton
                  size={"small"}
                  onClick={() => onStartEdit(r)}
                  sx={{
                    color: "text.disabled",
                    "&:hover": { color: "text.secondary" },
                    ml: "4px",
                  }}
                >
                  <EditIcon sx={{ fontSize: "0.75rem" }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>

      {/* Edit form — label/input rows */}
      {isEditing && (
        <>
          <Stack spacing={1.5} mt={2}>
            {/* Outcome */}
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <Typography
                variant={"caption"}
                color={"text.secondary"}
                sx={{ width: 88, flexShrink: 0 }}
              >
                {"Outcome"}
              </Typography>
              <Select
                size={"small"}
                value={draft.outcome}
                onChange={(e) =>
                  onDraftChange({ ...draft, outcome: e.target.value })
                }
                sx={(theme) => ({
                  fontSize: "0.85rem",
                  minWidth: 140,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: accentColor
                      ? alpha(theme.palette[accentColor].main, 0.55)
                      : undefined,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: accentColor
                      ? theme.palette[accentColor].main
                      : undefined,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: accentColor
                      ? theme.palette[accentColor].main
                      : undefined,
                  },
                })}
              >
                {Object.entries(OUTCOME_META).map(([key, m]) => (
                  <MenuItem key={key} value={key} sx={{ fontSize: "0.85rem" }}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </Stack>

            {/* Value */}
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <Typography
                variant={"caption"}
                color={"text.secondary"}
                sx={{ width: 88, flexShrink: 0 }}
              >
                {"Value"}
              </Typography>
              <TextField
                size={"small"}
                fullWidth
                value={draft.extracted_value}
                onChange={(e) =>
                  onDraftChange({ ...draft, extracted_value: e.target.value })
                }
                placeholder={"Extracted value (optional)"}
                inputProps={{ style: { fontSize: "0.85rem" } }}
              />
            </Stack>

            {/* Reasoning */}
            <Stack direction={"row"} spacing={2} alignItems={"flex-start"}>
              <Typography
                variant={"caption"}
                color={"text.secondary"}
                sx={{ width: 88, flexShrink: 0, mt: "9px" }}
              >
                {"Reasoning"}
              </Typography>
              <TextField
                size={"small"}
                fullWidth
                multiline
                minRows={3}
                value={draft.reasoning}
                onChange={(e) =>
                  onDraftChange({ ...draft, reasoning: e.target.value })
                }
                placeholder={"Your reasoning for this override"}
                inputProps={{ style: { fontSize: "0.85rem" } }}
              />
            </Stack>
          </Stack>

          {/* Footer: divider + Cancel / Apply */}
          <Divider sx={{ mt: 2, mx: "-16px" }} />
          <Stack
            direction={"row"}
            justifyContent={"flex-end"}
            spacing={1}
            mt={1.5}
          >
            <MuiButton
              size={"small"}
              variant={"outlined"}
              onClick={onCancelEdit}
              sx={{
                textTransform: "none",
                fontSize: "0.82rem",
                borderColor: "divider",
                color: "text.secondary",
                "&:hover": {
                  borderColor: "action.disabled",
                  bgcolor: "action.hover",
                },
              }}
            >
              {"Cancel"}
            </MuiButton>
            <MuiButton
              size={"small"}
              variant={"contained"}
              startIcon={<CheckIcon sx={{ fontSize: "0.8rem !important" }} />}
              onClick={() => onSaveDraft(r)}
              sx={{
                textTransform: "none",
                fontSize: "0.82rem",
                bgcolor: "text.primary",
                color: "background.paper",
                boxShadow: "none",
                "&:hover": { bgcolor: "action.active", boxShadow: "none" },
              }}
            >
              {"Apply"}
            </MuiButton>
          </Stack>
        </>
      )}
    </Box>
  );
};

// ─── Shared hook: override management ────────────────────────────────────────

const useTriageOverrides = (result, fileId, folderId) => {
  const [
    saveTriageOverrides,
    { isLoading: isSaving },
  ] = useSaveTriageOverridesMutation();
  const [overrides, setOverrides] = useState({});
  const [unsavedFields, setUnsavedFields] = useState(new Set());
  const [editingField, setEditingField] = useState(null);
  const [draft, setDraft] = useState(null);
  // The reviewer's own scope decision, or null to let the score and threshold decide.
  const [verdictOverride, setVerdictOverride] = useState(null);

  useEffect(() => {
    setVerdictOverride(result?.verdict_override ?? null);
  }, [result?.verdict_override]);

  useEffect(() => {
    if (!result?.overrides?.length) {
      setOverrides({});
      setUnsavedFields(new Set());
      return;
    }
    const map = {};
    for (const o of result.overrides) {
      map[o.field_name] = o;
    }
    setOverrides(map);
    setUnsavedFields(new Set());
  }, [result]);

  const handleStartEdit = (r) => {
    const existing = overrides[r.field_name];
    setDraft({
      outcome: existing?.user_outcome ?? r.outcome,
      extracted_value:
        existing?.user_extracted_value ?? r.extracted_value ?? "",
      reasoning: existing?.user_reasoning ?? r.reasoning,
    });
    setEditingField(r.field_name);
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setDraft(null);
  };

  const handleSaveDraft = (r) => {
    const now = new Date().toISOString();
    setOverrides((prev) => ({
      ...prev,
      [r.field_name]: {
        field_name: r.field_name,
        ai_outcome: r.outcome,
        ai_extracted_value: r.extracted_value ?? null,
        ai_reasoning: r.reasoning,
        user_outcome: draft.outcome,
        user_extracted_value: draft.extracted_value || null,
        user_reasoning: draft.reasoning,
        overridden_at: now,
      },
    }));
    setUnsavedFields((prev) => new Set([...prev, r.field_name]));
    setEditingField(null);
    setDraft(null);
  };

  // Set the scope decision and persist it at once. There is no draft step: the menu has
  // no Apply, so a decision left unsaved would look identical to a saved one. `null`
  // hands the verdict back to the score — storing an override equal to the derived answer
  // would freeze a decision nobody made and stop it tracking the threshold.
  const handleSetVerdict = async (userInScope, derivedInScope, context) => {
    if (!fileId && !folderId) return;
    const next =
      userInScope === null || userInScope === derivedInScope
        ? null
        : {
            user_in_scope: userInScope,
            derived_in_scope: derivedInScope ?? null,
            weighted_score: context?.weightedScore ?? null,
            in_scope_threshold: context?.threshold ?? null,
            user_reasoning: "",
            overridden_at: new Date().toISOString(),
          };
    const previous = verdictOverride;
    setVerdictOverride(next);
    try {
      const saved = await saveTriageOverrides({
        fileId: folderId ? null : fileId,
        folderId: folderId || null,
        overrides: Object.values(overrides),
        verdictOverride: next,
      }).unwrap();
      // Adopt what the server stored, not what was sent: the identity stamps are added
      // there, so the badge would otherwise show an unattributed decision until the next
      // refetch.
      setVerdictOverride(saved?.verdict_override ?? next);
      setUnsavedFields(new Set());
    } catch (e) {
      // Put the badge back rather than showing a decision the server never took.
      setVerdictOverride(previous);
      logger.error("Failed to save the scope decision", e);
    }
  };

  const handleSaveAll = async () => {
    if (!fileId && !folderId) return;
    const overrideList = Object.values(overrides);
    try {
      await saveTriageOverrides({
        fileId: folderId ? null : fileId,
        folderId: folderId || null,
        overrides: overrideList,
        verdictOverride: verdictOverride,
      }).unwrap();
      setUnsavedFields(new Set());
    } catch (e) {
      logger.error("Failed to save triage overrides", e);
    }
  };

  return {
    overrides,
    unsavedFields,
    editingField,
    draft,
    isSaving,
    handleStartEdit,
    handleCancelEdit,
    handleSaveDraft,
    handleSaveAll,
    setDraft,
    verdictOverride,
    handleSetVerdict,
  };
};

// ─── Rule list content ────────────────────────────────────────────────────────

const RuleResultsContent = ({
  rule_results,
  overrides,
  editingField,
  draft,
  isSaving,
  unsavedFields,
  fileId,
  folderId,
  onStartEdit,
  onCancelEdit,
  onSaveDraft,
  onSaveAll,
  onDraftChange,
  emailBodyFileId,
  onOpenEmail,
  attachmentFiles,
  webCompanySummary,
}) => (
  <Stack spacing={1}>
    {webCompanySummary && (
      <Typography
        variant={"caption"}
        color={"text.secondary"}
        sx={{ fontStyle: "italic", lineHeight: 1.5 }}
      >
        {`Web research: ${webCompanySummary}`}
      </Typography>
    )}
    {rule_results.map((r, idx) => {
      const override = overrides[r.field_name];
      const isEditing = editingField === r.field_name;
      return (
        <RuleCard
          key={idx}
          r={r}
          override={override}
          editingField={editingField}
          draft={isEditing ? draft : null}
          onStartEdit={onStartEdit}
          onCancelEdit={onCancelEdit}
          onDraftChange={onDraftChange}
          onSaveDraft={onSaveDraft}
          emailBodyFileId={emailBodyFileId}
          onOpenEmail={onOpenEmail}
          attachmentFiles={attachmentFiles}
        />
      );
    })}

    {(fileId || folderId) && unsavedFields.size > 0 && (
      <Stack direction={"row"} justifyContent={"flex-end"} mt={1}>
        <Button
          size={"small"}
          startIcon={<SaveIcon />}
          onClick={onSaveAll}
          isLoading={isSaving}
          isDisabled={isSaving}
        >
          {"Save overrides"}
        </Button>
      </Stack>
    )}
  </Stack>
);

// ─── Weighted score + per-layer scores ─────────────────────────────────────────

// What an `uncertain` entry earns, as a fraction of its weight. Mirrors UNCERTAIN_CREDIT
// in services/ai_agents/submission_triage/definitions.py — change both together or the
// figure the backend stored and the one recomputed here will disagree.
const UNCERTAIN_CREDIT = 0.0;

const earnedFor = (outcome, weight) => {
  if (outcome === "pass") return weight;
  if (outcome === "uncertain") return weight * UNCERTAIN_CREDIT;
  return 0;
};

// Half-UP to one decimal, matching compute_weighted_score. Math.round is already
// half-up, unlike Python's round(), which is why the Python side hand-rolls it.
const roundScore = (earned, total) => Math.round((1000 * earned) / total) / 10;

// An entry's outcome after any user override. Rules join on field_name; a combined
// rule has no field, so it joins on the label its synthesised row carries.
const outcomeForComponent = (component, overrides) => {
  const key = component.field_name || component.label;
  const override = overrides?.[key];
  return override ? override.user_outcome : component.outcome;
};

// Whether a weighted figure says anything the pass fraction does not.
//
// The backend's answer, never re-derived here: report ROWS carry accumulated weights (two
// top-level rules on one field share a single deduped row), so deriving it from rows
// disagreed with the backend's per-ENTRY view — a figure the backend withheld appeared the
// moment a user overrode a row, and vice versa.
const weightsAreInformative = (report) => report?.weights_informative === true;

// The report's own weighted score, recomputed only once a human has overridden
// something. The backend scores every top-level entry, including any whose rule
// produced no row, so its figure is authoritative until an override changes an
// outcome — recomputing unconditionally would show a different number for the
// same untouched report. Null when nothing carries weight, and the caller renders
// nothing.
const computeEffectiveWeightedScore = (report, overrides) => {
  const stored =
    typeof report?.weighted_score === "number" ? report.weighted_score : null;
  if (!overrides || Object.keys(overrides).length === 0) return stored;
  if (!weightsAreInformative(report)) return stored;

  // Per-entry, from the breakdown the backend built alongside the score itself. Rows
  // cannot serve: two top-level rules on one field share a single deduped row.
  const components = report?.score_breakdown || [];
  if (components.length === 0) return stored;
  const total = components.reduce((sum, c) => sum + c.weight, 0);
  if (total <= 0) return stored;
  const earned = components.reduce(
    (sum, c) => sum + earnedFor(outcomeForComponent(c, overrides), c.weight),
    0
  );
  return roundScore(earned, total);
};

// The pass fraction, counted the way the report is scored: one per top-level entry, the
// same unit as passed_count/total_count and the pipeline row beside it. Rows are the wrong
// unit — a combined rule contributes a row per child plus its own synthesised row, so
// counting rows read 3/3 where the list read 1/1.
export const effectiveCounts = (report, overrides) => {
  const components = report?.score_breakdown || [];
  return {
    passed: components.filter(
      (c) => outcomeForComponent(c, overrides) === "pass"
    ).length,
    total: components.length,
  };
};

// The score breakdown with each entry's effective outcome and contribution, so the
// explainer always shows the arithmetic behind the figure actually on screen.
const effectiveBreakdown = (report, overrides) =>
  (report?.score_breakdown || []).map((c) => {
    const outcome = outcomeForComponent(c, overrides);
    return {
      ...c,
      effective_outcome: outcome,
      effective_earned: earnedFor(outcome, c.weight),
      overridden: outcome !== c.outcome,
    };
  });

// Per-layer scores with user overrides applied.
//
// Submission-wide entries contribute the same outcome to every layer, so overriding one
// moves every layer — without this the headline moved and the layer rows stayed frozen,
// two contradictory scores in one view. Layer-scoped components are left alone on
// purpose: a per-layer verdict is code-evaluated against the tower and not editable.
const recomputeLayerScores = (layerScores, overrides) => {
  if (!layerScores || layerScores.length === 0) return layerScores;
  if (!overrides || Object.keys(overrides).length === 0) return layerScores;
  return layerScores.map((layer) => {
    const components = (layer.components || []).map((c) => {
      // Layer-scoped by the field's NAME, not by what the breakdown happens to list: the
      // tower's verdict for this layer stands whatever the user did to the rule's row.
      if (LAYER_FIELDS.has(c.field_name)) return c;
      const override = overrides?.[c.field_name];
      return override ? { ...c, outcome: override.user_outcome } : c;
    });
    const total = components.reduce((sum, c) => sum + c.weight, 0);
    const earned = components.reduce(
      (sum, c) => sum + earnedFor(c.outcome, c.weight),
      0
    );
    return {
      ...layer,
      components,
      score: total > 0 ? roundScore(earned, total) : layer.score,
    };
  });
};

// A layer's score, worked out the way the panel shows it. Same arithmetic as the headline
// breakdown, against this layer's own component outcomes: a passing entry earns its weight,
// anything else earns nothing, and the score is the earned share of the possible.
const layerBreakdown = (layer) => {
  const components = (layer.components || []).map((c) => ({
    label: c.field_name,
    weight: c.weight,
    outcome: c.outcome,
    earned: earnedFor(c.outcome, c.weight),
  }));
  const earned = components.reduce((sum, c) => sum + c.earned, 0);
  const possible = components.reduce((sum, c) => sum + c.weight, 0);
  return { components, earned, possible };
};

// Marks a report built by amending an earlier one, and says how much of it is carried
// over. Without this a reader cannot tell that most of the report predates the documents
// the item now holds.
const IncrementalRunNote = ({ report }) => {
  if (report?.run_mode !== "incremental") return null;
  const rows = report?.rule_results || [];
  // A re-decided row is one the new documents actually spoke to; a carried row keeps the
  // earlier run's reasoning and citations. The carried figure is what tells the reader how
  // much of the report predates the documents the item now holds, which is the whole
  // reason this marker exists.
  const restated = rows.filter(
    (r) => r.confidence === "low" || r.prior_value != null
  ).length;
  const carried = Math.max(rows.length - restated, 0);
  return (
    <Tooltip
      title={
        "Only the newly added documents were read. Rules they did not bear on keep the conclusions of the earlier run."
      }
      arrow
    >
      <Typography
        sx={{
          fontSize: 11,
          color: "text.disabled",
          mb: 0.75,
          cursor: "default",
          display: "inline-block",
        }}
      >
        {restated > 0
          ? `Amended from an earlier run — ${restated} rule${
              restated === 1 ? "" : "s"
            } re-decided, ${carried} carried over`
          : `Amended from an earlier run — nothing re-decided, all ${carried} carried over`}
      </Typography>
    </Tooltip>
  );
};

// Whether the report reads as passing.
//
// The backend's overall_outcome is authoritative and is used as-is until a human overrides
// something: it applies the real gating rules, which are not "every row passed". A rule
// that is not `required` does not gate, and a flat LAYER rule never gates however required
// is set — layer rules score, they do not gate. Counting every row instead showed Fail in
// the drawer while the pipeline row beside it showed the backend's Pass, most visibly for a
// layer rule whose tower was not extracted (outcome missing, correctly non-gating).
//
// Once overrides exist the verdict has to move with them, so it is recomputed — but only
// over the entries that actually gate, mirroring the backend.

// The scope verdict, and the control that lets a reviewer overrule it.
//
// Exported because two variants of this dialog render the verdict and a decision made in
// one has to read the same in the other. `onToggle` is omitted on the upload-drawer
// variant, which has no item to save against.
// The address without its plus-suffix: "lucas.schneider+xsc-u@cactusrisk.com" reads as
// "lucas.schneider@cactusrisk.com". The suffix routes mail to a test tenant and says
// nothing about who someone is. The full address is what gets stored; this is display
// only, so the record keeps the address the mail actually went to.
export const displayEmail = (email) => {
  if (!email) return "";
  const [local, domain] = email.split("@");
  const base = local.split("+")[0];
  return domain ? `${base}@${domain}` : base;
};

// Initials for the avatar on an overruled verdict: "lucas.schneider+xsc-u@..." reads as
// LS. Falls back to the first character when there is no separator to split on.
export const initialsFrom = (email) => {
  const local = (email || "").split("@")[0].split("+")[0];
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (!parts.length) return "?";
  const letters =
    parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2);
  return letters.toUpperCase();
};

// The scope verdict, and the menu that lets a reviewer overrule it.
//
// A chevron rather than a plain toggle: the decision has three states — in scope, out of
// scope, and "whatever the score says" — and a toggle cannot express the third. An
// overruled verdict is ringed and carries the initials of whoever set it, so a reader can
// tell a human decision from an arithmetic one at a glance across a list.
export const VerdictBadge = ({
  isPassing,
  derivedInScope,
  verdictOverride,
  onSetVerdict,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const overridden = !!verdictOverride;
  const who = verdictOverride?.overridden_by_email;
  const when = verdictOverride?.overridden_at
    ? new Date(verdictOverride.overridden_at).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;
  const context =
    verdictOverride?.weighted_score != null &&
    verdictOverride?.in_scope_threshold != null
      ? ` · scored ${verdictOverride.weighted_score}% against a ${verdictOverride.in_scope_threshold}% threshold`
      : "";
  const tooltip = !overridden
    ? "Set by the score. Click to decide it yourself."
    : who
    ? `Set by ${displayEmail(who)}${when ? ` on ${when}` : ""}${context}`
    : `Set by a reviewer${when ? ` on ${when}` : ""}${context}`;

  const choose = (value) => {
    setMenuAnchor(null);
    onSetVerdict(value);
  };

  return (
    <>
      <Tooltip title={onSetVerdict ? tooltip : ""} arrow>
        <Box
          component={onSetVerdict ? "button" : "div"}
          aria-label={onSetVerdict ? "Change the scope decision" : undefined}
          onClick={
            onSetVerdict ? (e) => setMenuAnchor(e.currentTarget) : undefined
          }
          sx={(theme) => ({
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            height: 24,
            pl: overridden ? "3px" : "9px",
            pr: onSetVerdict ? "5px" : "9px",
            border: overridden
              ? `1px solid ${theme.palette.text.primary}`
              : "1px solid transparent",
            borderRadius: "12px",
            fontSize: 11.5,
            fontWeight: 500,
            font: "inherit",
            cursor: onSetVerdict ? "pointer" : "default",
            bgcolor: isPassing
              ? alpha(theme.palette.success.main, 0.12)
              : alpha(theme.palette.text.primary, 0.07),
            color: isPassing ? "success.dark" : "text.secondary",
          })}
        >
          {overridden ? (
            <Box
              data-testid={"verdict-override-initials"}
              sx={(theme) => ({
                width: 18,
                height: 18,
                borderRadius: "50%",
                bgcolor: theme.palette.text.primary,
                color: theme.palette.background.paper,
                fontSize: 8.5,
                fontWeight: 700,
                letterSpacing: "0.02em",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              })}
            >
              {initialsFrom(who)}
            </Box>
          ) : (
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                bgcolor: isPassing ? "success.main" : "text.disabled",
                flexShrink: 0,
              }}
            />
          )}
          {isPassing ? "In scope" : "Out of scope"}
          {onSetVerdict && (
            <ChevronDownIcon sx={{ fontSize: 14, opacity: 0.5, ml: "-1px" }} />
          )}
        </Box>
      </Tooltip>
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          selected={isPassing}
          onClick={() => choose(true)}
          sx={{ fontSize: 12.5 }}
        >
          {"In scope"}
        </MenuItem>
        <MenuItem
          selected={!isPassing}
          onClick={() => choose(false)}
          sx={{ fontSize: 12.5 }}
        >
          {"Out of scope"}
        </MenuItem>
        {overridden && (
          <MenuItem onClick={() => choose(null)} sx={{ fontSize: 12.5 }}>
            {`Use the score's answer (${
              derivedInScope ? "in scope" : "out of scope"
            })`}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export const isReportPassing = (
  report,
  overrides,
  threshold = null,
  verdictOverride = null
) => {
  // A reviewer's own decision outranks the score, the threshold and the gating rules:
  // it is the one verdict a person put their name to. Tracked separately so the score
  // beside it still reads as what the documents produced.
  if (verdictOverride) return !!verdictOverride.user_in_scope;
  const stored = report?.overall_outcome;
  const hasOverrides = overrides && Object.keys(overrides).length > 0;
  // A ruleset with an in-scope threshold is classified by its score alone, against the
  // CURRENT threshold — so editing it reclassifies the report with no re-run, and no
  // single rule hard-stops the submission. The score is the override-aware one on the
  // summary line, so the verdict and the figure above it always agree.
  const byThreshold = isInScope(
    computeEffectiveWeightedScore(report, overrides),
    threshold
  );
  if (byThreshold !== null) return byThreshold;
  if (!hasOverrides) return stored ? stored === "pass" : null;

  const components = report?.score_breakdown || [];
  if (components.length === 0) return stored ? stored === "pass" : null;
  const blocking = new Set(["fail", "missing"]);
  return !components.some((c) => {
    // `gates` is the backend's own answer, layer rules included. A rule that is not
    // required scores without gating, so counting it here showed Fail beside the
    // pipeline row's Pass.
    if (!c.gates) return false;
    const key = c.field_name || c.label;
    const ov = overrides?.[key];
    return blocking.has(ov ? ov.user_outcome : c.outcome);
  });
};

// The column header both breakdowns share. Without it the weight column reads as a bare
// number, which is why the old tooltip had to write "w4" — and a reader still had to guess
// that w meant weight.
const BREAKDOWN_COLUMNS = { weight: 56, outcome: 84, score: 56 };

const BreakdownHeader = () => (
  <Stack
    direction={"row"}
    alignItems={"center"}
    spacing={1.5}
    sx={(theme) => ({
      pb: 0.5,
      mb: 0.25,
      borderBottom: `1px solid ${theme.palette.divider}`,
    })}
  >
    <Typography
      sx={{ fontSize: 10, color: "text.disabled", flex: 1, minWidth: 0 }}
    >
      {"RULE"}
    </Typography>
    <Typography
      sx={{
        fontSize: 10,
        color: "text.disabled",
        width: BREAKDOWN_COLUMNS.weight,
        textAlign: "center",
      }}
    >
      {"WEIGHT"}
    </Typography>
    <Typography
      sx={{
        fontSize: 10,
        color: "text.disabled",
        width: BREAKDOWN_COLUMNS.outcome,
        textAlign: "center",
      }}
    >
      {"OUTCOME"}
    </Typography>
    <Typography
      sx={{
        fontSize: 10,
        color: "text.disabled",
        width: BREAKDOWN_COLUMNS.score,
        textAlign: "right",
      }}
    >
      {"SCORE"}
    </Typography>
  </Stack>
);

// One row of either breakdown. The rule name carries what the rule REQUIRES on hover — the
// report records outcomes, so without it a reader sees "Premium: pass" and still has to open
// the ruleset to learn what passing meant.
const BreakdownRow = ({ label, requirement, weight, outcome, score, note }) => {
  // A rule that earned nothing is why the score is not full marks, so it reads in its
  // outcome's own colour. Every number here is positive-or-zero, and a column of black
  // zeros made the reader compare weights to find the one that cost the points.
  const earnedNothing = !(score > 0);
  const accent =
    (OUTCOME_META[outcome] || {}).color === "default" || !OUTCOME_META[outcome]
      ? "warning"
      : OUTCOME_META[outcome].color;
  return (
    <Stack direction={"row"} alignItems={"center"} spacing={1.5}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Tooltip
          title={requirement || ""}
          arrow
          disableHoverListener={!requirement}
        >
          <Typography
            data-requirement={requirement || undefined}
            sx={{
              fontSize: 12,
              display: "inline-block",
              maxWidth: "100%",
              cursor: requirement ? "help" : "default",
              borderBottom: requirement ? "1px dotted" : "none",
              borderColor: "divider",
            }}
            noWrap
          >
            {label}
          </Typography>
        </Tooltip>
        {note}
      </Box>
      <Typography
        sx={{
          fontSize: 11.5,
          color: "text.secondary",
          width: BREAKDOWN_COLUMNS.weight,
          textAlign: "center",
        }}
      >
        {weight}
      </Typography>
      <Typography
        sx={{
          fontSize: 11.5,
          color: earnedNothing ? `${accent}.dark` : "text.secondary",
          width: BREAKDOWN_COLUMNS.outcome,
          textAlign: "center",
        }}
      >
        {outcome}
      </Typography>
      <Typography
        aria-label={label ? `${label} score` : undefined}
        sx={{
          fontSize: 12,
          width: BREAKDOWN_COLUMNS.score,
          textAlign: "right",
          fontWeight: 600,
          color: earnedNothing ? `${accent}.dark` : "text.primary",
        }}
      >
        {score > 0 ? `+${score}` : "0"}
      </Typography>
    </Stack>
  );
};

// "Total score · 9 of 11 points · 81.8%". The arithmetic is the point of the panel, so the
// points and the percentage sit together rather than making the reader derive one.
const BreakdownTotal = ({ earned, possible, score }) => (
  <Stack direction={"row"} alignItems={"center"} spacing={1.5}>
    <Typography sx={{ fontSize: 12, flex: 1, fontWeight: 600, minWidth: 0 }}>
      {"Total score"}
    </Typography>
    <Typography
      sx={{
        fontSize: 11.5,
        color: "text.secondary",
        width: BREAKDOWN_COLUMNS.weight + BREAKDOWN_COLUMNS.outcome + 12,
        textAlign: "center",
      }}
    >
      {`${earned} of ${possible} points`}
    </Typography>
    <Typography
      aria-label={"Total score"}
      sx={{
        fontSize: 12,
        fontWeight: 700,
        width: BREAKDOWN_COLUMNS.score,
        textAlign: "right",
        // Full marks read as ordinary text; anything less is the number the reader came
        // for, and it should not look the same as 100%.
        color: earned < possible ? "warning.dark" : "text.primary",
      }}
    >
      {score == null ? "—" : `${score}%`}
    </Typography>
  </Stack>
);

// "How this score was calculated" — the arithmetic behind the figure on the summary
// line, so a reader can check it by hand instead of taking a percentage on trust.
// Rendered only when the backend judged the weights informative, matching the rule that
// decides whether the figure is shown at all.
const ScoreBreakdownBlock = ({
  report,
  overrides,
  score,
  requirements = {},
}) => {
  const [open, setOpen] = useState(false);
  const components = effectiveBreakdown(report, overrides);
  if (
    score == null ||
    components.length === 0 ||
    !weightsAreInformative(report)
  )
    return null;

  const earned = components.reduce((sum, c) => sum + c.effective_earned, 0);
  const possible = components.reduce((sum, c) => sum + c.weight, 0);

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box
        component={"button"}
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "3px",
          background: "none",
          border: "none",
          p: 0,
          cursor: "pointer",
          color: "text.secondary",
          fontSize: 11,
          "&:hover": { color: "text.primary" },
        }}
      >
        {open ? (
          <ChevronUpIcon sx={{ fontSize: "0.85rem" }} />
        ) : (
          <ChevronDownIcon sx={{ fontSize: "0.85rem" }} />
        )}
        {"How this score was calculated"}
      </Box>
      {open && (
        <Stack
          spacing={0.25}
          sx={(theme) => ({
            mt: 0.75,
            p: "10px 12px",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            fontVariantNumeric: "tabular-nums",
          })}
        >
          <BreakdownHeader />
          {components.map((c, idx) => (
            <BreakdownRow
              key={idx}
              label={fieldDisplayName(c.label)}
              requirement={requirements[c.field_name || c.label]}
              weight={c.weight}
              outcome={c.effective_outcome}
              score={c.effective_earned}
              note={
                c.overridden ? (
                  <Typography
                    component={"span"}
                    sx={{ fontSize: 10.5, color: "warning.main", ml: 0.5 }}
                  >
                    {"(your edit)"}
                  </Typography>
                ) : null
              }
            />
          ))}
          <Divider sx={{ my: 0.5 }} />
          <BreakdownTotal earned={earned} possible={possible} score={score} />
        </Stack>
      )}
    </Box>
  );
};

// The item's documents as they are NOW, and which of them the report in front of you
// actually read. The bug this whole feature exists to fix was a report claiming to cover a
// six-document submission while computed from one, and until now that claim was not
// verifiable anywhere in the triage UI.
// Mirrors the server's refusal to remove the last READABLE document: a .zip container
// pads the file list but nothing can read it. A container is neither read nor outstanding —
// the server extracts its members into the folder as separate documents and never reads the
// archive itself, so counting it strands the tally one short forever and marks it unread,
// inviting a re-run that cannot change anything. Same reason the server derives staleness
// from triageable_file_ids() rather than every entry in folder.files.
const isReadableDocument = (f) => !/\.zip$/i.test(f?.file_name || "");

// The item's documents, each tagged with whether the report in front of you read it.
// Shared because the Documents block, the stale pill and the re-run button all state the
// same fact; three places deriving it separately is how they come to disagree.
const documentsWithReadState = (item) => {
  const readIds = new Set(item?.triage_read_file_ids || []);
  return (item?.current_files || []).map((f) => ({
    ...f,
    readable: isReadableDocument(f),
    // An empty read record means unknown, not none, as on the server and the pipeline row.
    read:
      isReadableDocument(f) && (readIds.size === 0 || readIds.has(f.file_id)),
  }));
};

// Documents that have LEFT the item since the report in front of you was computed. The
// server records them on detach (DetachedFileRecord) and forces the next run to be a full
// re-read; this is the display half of the same fact, so the reader is told the document set
// CHANGED rather than merely grew. Compared against the report's completion time for the
// same reason the server does: a departure the stored report already accounts for is history,
// not an outstanding change.
const documentsRemovedSinceReport = (item, report) => {
  const records = item?.detached_files || [];
  if (records.length === 0) return [];
  const completedAt = Date.parse(item?.triage_completed_at || "");
  // Also filtered by what the report READ, as documents_detached_since does: losing a
  // document this report never opened orphans nothing, so prompting for it offers a
  // re-run that would change nothing.
  const readIds = new Set(item?.triage_read_file_ids || []);
  return records
    .filter((record) => readIds.has(record.file_id))
    .filter((record) => {
      const leftAt = Date.parse(record.detached_at || "");
      // An unknown time counts as later, matching the server: a needless prompt to re-run
      // costs a click, a missed one leaves a report resting on a document that is gone.
      if (Number.isNaN(leftAt) || Number.isNaN(completedAt)) return true;
      return leftAt > completedAt;
    })
    .map((record) => record.file_name)
    .filter(Boolean);
};

// How many attached documents the current report has not read. This is what the re-run
// button counts, so it must never include an archive.
const unreadDocumentCount = (item) =>
  documentsWithReadState(item).filter((f) => f.readable && !f.read).length;

const COLLAPSED_DOCUMENTS = 5;

const DocumentsBlock = ({ item, onDetachFile, onAddFiles, onDownloadFile }) => {
  const [draggingOver, setDraggingOver] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const current = item?.current_files || [];
  // With no documents the block is the only way to attach one, so it still renders for the
  // Add control. Without that an item that lost its file list had no attach affordance at
  // all once the footer button moved in here.
  if (current.length === 0 && !onAddFiles) return null;
  const files = documentsWithReadState(item);
  const readableCount = files.filter(isReadableDocument).length;
  const readCount = files.filter((f) => f.read).length;
  const removableFrom = readableCount;
  const removable = (f) =>
    Boolean(onDetachFile) &&
    Boolean(item?.folder_id) &&
    removableFrom - (isReadableDocument(f) ? 1 : 0) >= 1;
  const hiddenCount = files.length - COLLAPSED_DOCUMENTS;
  const collapsed = hiddenCount > 0 && !showAll;
  const shownFiles = collapsed ? files.slice(0, COLLAPSED_DOCUMENTS) : files;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        mb={0.75}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: "text.disabled",
          }}
        >
          {readCount === readableCount
            ? `Documents (${files.length})`
            : `Documents — ${readCount} of ${readableCount} read by this report`}
        </Typography>
        {hiddenCount > 0 && (
          <Box
            component={"button"}
            type={"button"}
            onClick={() => setShowAll((v) => !v)}
            aria-expanded={showAll}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "3px",
              background: "none",
              border: "none",
              cursor: "pointer",
              p: 0,
              color: "text.secondary",
              fontSize: 11,
              fontFamily: "inherit",
              "&:hover": { color: "text.primary" },
            }}
          >
            {showAll ? (
              <>
                <ChevronUpIcon sx={{ fontSize: "0.85rem" }} />
                {"Show less"}
              </>
            ) : (
              <>
                <ChevronDownIcon sx={{ fontSize: "0.85rem" }} />
                {`${hiddenCount} more`}
              </>
            )}
          </Box>
        )}
      </Stack>
      <Stack
        data-testid={"documents-dropzone"}
        direction={"row"}
        spacing={0.75}
        flexWrap={"wrap"}
        useFlexGap
        onDragOver={
          onAddFiles
            ? (e) => {
                e.preventDefault();
                setDraggingOver(true);
              }
            : undefined
        }
        onDragLeave={onAddFiles ? () => setDraggingOver(false) : undefined}
        onDrop={
          onAddFiles
            ? (e) => {
                e.preventDefault();
                setDraggingOver(false);
                const dropped = Array.from(e.dataTransfer?.files || []);
                // Handed to the attach dialog rather than uploaded from here, so a drop and
                // a click reach exactly the same validation, naming and confirmation.
                onAddFiles(dropped.length > 0 ? dropped : undefined);
              }
            : undefined
        }
        sx={(theme) => ({
          p: "10px 12px",
          border: `1px ${draggingOver ? "dashed" : "solid"} ${
            draggingOver ? theme.palette.primary.main : theme.palette.divider
          }`,
          borderRadius: 2,
          bgcolor: draggingOver
            ? alpha(theme.palette.primary.main, 0.04)
            : "transparent",
          transition: "background-color 120ms, border-color 120ms",
        })}
      >
        {shownFiles.map((f) => (
          <Stack
            key={f.file_id}
            direction={"row"}
            alignItems={"center"}
            spacing={0.5}
            sx={(theme) => ({
              px: "8px",
              py: "3px",
              borderRadius: "4px",
              fontSize: 11.5,
              bgcolor: alpha(theme.palette.text.primary, 0.04),
              border: `1px solid ${theme.palette.divider}`,
              // The × is revealed by hover or keyboard focus rather than sitting on every
              // chip: a row of always-visible delete controls reads as the point of the
              // block, when the point is which documents the report covered.
              "&:hover .doc-remove, &:focus-within .doc-remove": { opacity: 1 },
            })}
          >
            <Tooltip
              title={
                !f.readable
                  ? "Archive — its contents were extracted into this submission as separate documents"
                  : f.read
                  ? "Read by this report"
                  : "Added since this report ran — re-run to include it"
              }
              arrow
            >
              <FileIcon
                sx={{
                  fontSize: "0.7rem",
                  color:
                    f.read || !f.readable ? "text.disabled" : "warning.main",
                }}
              />
            </Tooltip>
            <Tooltip
              title={onDownloadFile ? `Download ${f.file_name}` : ""}
              arrow
              enterDelay={400}
            >
              <Typography
                component={onDownloadFile ? "button" : "span"}
                type={onDownloadFile ? "button" : undefined}
                onClick={onDownloadFile ? () => onDownloadFile(f) : undefined}
                sx={{
                  fontSize: 11.5,
                  color: "text.secondary",
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  border: "none",
                  background: "none",
                  p: 0,
                  fontFamily: "inherit",
                  ...(onDownloadFile && {
                    cursor: "pointer",
                    "&:hover": { color: "primary.main" },
                  }),
                }}
              >
                {f.file_name}
              </Typography>
            </Tooltip>
            {removable(f) && (
              <Tooltip title={"Remove from this submission"} arrow>
                <Box
                  component={"span"}
                  role={"button"}
                  tabIndex={0}
                  className={"doc-remove"}
                  aria-label={`Remove ${f.file_name}`}
                  onClick={() => onDetachFile(f)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onDetachFile(f);
                    }
                  }}
                  sx={{
                    display: "inline-flex",
                    cursor: "pointer",
                    color: "text.disabled",
                    opacity: 0,
                    transition: "opacity 120ms",
                    "&:hover": { color: "error.main" },
                    "&:focus-visible": { opacity: 1, color: "error.main" },
                  }}
                >
                  <XIcon sx={{ fontSize: "0.7rem" }} />
                </Box>
              </Tooltip>
            )}
          </Stack>
        ))}
        {onAddFiles && (
          <Tooltip
            title={"Add documents — or drop them anywhere in this box"}
            arrow
          >
            <Box
              component={"span"}
              role={"button"}
              tabIndex={0}
              aria-label={"Add documents"}
              onClick={() => onAddFiles()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onAddFiles();
                }
              }}
              sx={(theme) => ({
                display: "inline-flex",
                alignItems: "center",
                gap: "3px",
                px: "8px",
                py: "3px",
                borderRadius: "4px",
                fontSize: 11.5,
                cursor: "pointer",
                color: "text.secondary",
                border: `1px dashed ${theme.palette.divider}`,
                "&:hover": {
                  color: "primary.main",
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              })}
            >
              <PlusIcon sx={{ fontSize: "0.8rem" }} />
              {"Add"}
            </Box>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};

// One pricing layer, expandable. The component outcomes used to be crammed into a tooltip
// as "Premium: pass (w4) · …", which is unreadable at five rules and unreachable on a touch
// device — and it made the reader decode "w4" with no column to tell them it was a weight.
const LayerScoreRow = ({ layer, requirements = {} }) => {
  const [open, setOpen] = useState(false);
  const { components, earned, possible } = layerBreakdown(layer);
  const scoreColour =
    (layer.score ?? 0) >= 75
      ? "success"
      : (layer.score ?? 0) >= 40
      ? "warning"
      : "error";

  return (
    <Box>
      <Stack
        component={components.length > 0 ? "button" : "div"}
        direction={"row"}
        alignItems={"center"}
        spacing={1.25}
        onClick={components.length > 0 ? () => setOpen((v) => !v) : undefined}
        aria-expanded={components.length > 0 ? open : undefined}
        aria-label={
          components.length > 0 ? `How ${layer.label} scored` : undefined
        }
        sx={{
          width: "100%",
          background: "none",
          border: "none",
          p: 0,
          textAlign: "left",
          cursor: components.length > 0 ? "pointer" : "default",
          color: "inherit",
          font: "inherit",
        }}
      >
        {components.length > 0 ? (
          open ? (
            <ChevronUpIcon
              sx={{
                fontSize: "0.85rem",
                color: "text.disabled",
                flexShrink: 0,
              }}
            />
          ) : (
            <ChevronDownIcon
              sx={{
                fontSize: "0.85rem",
                color: "text.disabled",
                flexShrink: 0,
              }}
            />
          )
        ) : (
          <Box sx={{ width: "0.85rem", flexShrink: 0 }} />
        )}
        <Typography
          sx={{
            fontSize: 12,
            fontFamily: "monospace",
            width: 104,
            flexShrink: 0,
          }}
          noWrap
        >
          {layer.label}
        </Typography>
        {layer.coverage && (
          <Chip
            size={"small"}
            label={layer.coverage}
            sx={{ height: 16, fontSize: 9.5, flexShrink: 0 }}
          />
        )}
        <LinearProgress
          variant={"determinate"}
          value={layer.score ?? 0}
          color={scoreColour}
          sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: "action.hover" }}
        />
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            width: 44,
            textAlign: "right",
            flexShrink: 0,
          }}
        >
          {layer.score == null ? "—" : `${layer.score}%`}
        </Typography>
      </Stack>
      {open && (
        <Stack
          spacing={0.25}
          sx={{
            mt: 0.5,
            mb: 0.5,
            ml: "calc(0.85rem + 10px)",
            pr: "10px",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          <BreakdownHeader />
          {components.map((c, idx) => (
            <BreakdownRow
              key={idx}
              label={fieldDisplayName(c.label)}
              requirement={requirements[c.label]}
              weight={c.weight}
              outcome={c.outcome}
              score={c.earned}
            />
          ))}
          <Divider sx={{ my: 0.5 }} />
          <BreakdownTotal
            earned={earned}
            possible={possible}
            score={layer.score}
          />
          <Typography sx={{ fontSize: 10.5, color: "text.disabled", mt: 0.5 }}>
            {
              "Layer rules are judged against this layer; every other rule contributes the same outcome to all layers."
            }
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

const LayerScoresBlock = ({ layerScores, requirements = {} }) => {
  if (!layerScores || layerScores.length === 0) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "text.disabled",
          mb: 0.75,
        }}
      >
        {"Score by pricing layer"}
      </Typography>
      <Stack
        spacing={0.5}
        sx={(theme) => ({
          p: "10px 12px",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        })}
      >
        {layerScores.map((layer, idx) => (
          <LayerScoreRow key={idx} layer={layer} requirements={requirements} />
        ))}
      </Stack>
    </Box>
  );
};

// ─── Dialog variant (used by SubmissionFileUploadDrawer) ──────────────────────

const TriageResultDialogVariant = ({
  result,
  fileName,
  fileId,
  folderId,
  open,
  onClose,
}) => {
  const {
    overrides,
    unsavedFields,
    editingField,
    draft,
    isSaving,
    handleStartEdit,
    handleCancelEdit,
    handleSaveDraft,
    handleSaveAll,
    setDraft,
    verdictOverride,
    handleSetVerdict,
  } = useTriageOverrides(result, fileId, folderId);

  const handleClose = () => {
    setDraft && setDraft(null);
    onClose();
  };

  if (!result) return null;

  const { rule_results } = result.report;

  if (!rule_results) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={"sm"}
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}
      >
        <DialogContent sx={{ py: 4 }}>
          <LinearProgress />
        </DialogContent>
      </Dialog>
    );
  }
  const { passed: effectivePassed, total: total_count } = effectiveCounts(
    result?.report,
    overrides
  );
  // No ruleset prop on this variant, so the threshold the run recorded is the best
  // available; the drawer uses the ruleset's current one and reclassifies on an edit.
  const reportThreshold = result?.report?.in_scope_threshold ?? null;
  // The verdict the numbers reach on their own, so the badge knows what it is overruling
  // and a reviewer clicking back lands on it rather than on a stored copy of it.
  const derivedRaw = isReportPassing(
    result?.report,
    overrides,
    reportThreshold
  );
  const derivedPassing =
    derivedRaw === null ? effectivePassed === total_count : derivedRaw;
  const passing = isReportPassing(
    result?.report,
    overrides,
    reportThreshold,
    verdictOverride
  );
  const isPassing =
    passing === null ? effectivePassed === total_count : passing;
  const scorePercent =
    total_count > 0 ? Math.round((effectivePassed / total_count) * 100) : 0;
  const weightedScore = computeEffectiveWeightedScore(
    result?.report,
    overrides
  );
  // The bar sits under the weighted score, so it tracks that number. It fell back to the
  // rule fraction, which made a 27.3% item show a half-full bar.
  const barPercent =
    typeof weightedScore === "number"
      ? Math.round(weightedScore)
      : scorePercent;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={"sm"}
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3 } } }}
    >
      <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3 }}>
        <Stack direction={"row"} alignItems={"flex-start"} spacing={1.5}>
          <TriageSparkleIcon
            sx={{ fontSize: "0.8rem", color: "warning.main", mt: 0.5 }}
          />
          <Box flex={1} minWidth={0}>
            <Typography variant={"h6"} fontWeight={700} lineHeight={1.2}>
              {"Triage Report"}
            </Typography>
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              noWrap
              display={"block"}
              mt={0.25}
            >
              {fileName}
            </Typography>
          </Box>
          <IconButton
            size={"small"}
            onClick={handleClose}
            sx={{ mt: -0.5, color: "text.secondary" }}
          >
            <CloseIcon fontSize={"small"} />
          </IconButton>
        </Stack>

        {/* Summary */}
        <Box
          sx={(theme) => ({
            mt: 2,
            mb: 0.5,
            p: "14px 16px",
            borderRadius: 2,
            bgcolor: alpha(theme.palette.text.primary, 0.02),
            border: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            mb={1}
          >
            {/* The weighted score leads: it is the figure that reflects how the ruleset
                actually weighs its rules, and the pass fraction counts every entry alike.
                A ruleset with even weights withholds the weighted figure, so the fraction
                takes the lead there instead. */}
            <Stack direction={"row"} alignItems={"baseline"} spacing={0.5}>
              {weightedScore != null ? (
                <>
                  <Typography
                    aria-label={"Weighted score"}
                    sx={{
                      fontSize: 30,
                      fontWeight: 400,
                      letterSpacing: "-0.02em",
                      lineHeight: 1,
                      color: "text.primary",
                    }}
                  >
                    {`${weightedScore}%`}
                  </Typography>
                  <Typography variant={"body2"} color={"text.secondary"} ml={1}>
                    {"weighted score"}
                  </Typography>
                  <Typography variant={"body2"} color={"text.secondary"} ml={1}>
                    {`· ${effectivePassed}/${total_count} rules passed`}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    sx={{
                      fontSize: 30,
                      fontWeight: 400,
                      letterSpacing: "-0.02em",
                      lineHeight: 1,
                      color: "text.primary",
                    }}
                  >
                    {effectivePassed}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 20,
                      color: "text.disabled",
                      fontWeight: 400,
                    }}
                  >
                    {`/${total_count}`}
                  </Typography>
                  <Typography variant={"body2"} color={"text.secondary"} ml={1}>
                    {"rules passed"}
                  </Typography>
                </>
              )}
            </Stack>
            <VerdictBadge
              isPassing={isPassing}
              derivedInScope={derivedPassing}
              verdictOverride={verdictOverride}
              onSetVerdict={(value) =>
                handleSetVerdict(value, derivedPassing, {
                  weightedScore,
                  threshold: reportThreshold,
                })
              }
            />
          </Stack>
          <LinearProgress
            variant={"determinate"}
            value={barPercent}
            color={isPassing ? "success" : "inherit"}
            sx={{ height: 3, borderRadius: 2, bgcolor: "action.hover" }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5, px: 3, pb: 3 }}>
        <IncrementalRunNote report={result?.report} />
        <ScoreBreakdownBlock
          report={result?.report}
          overrides={overrides}
          score={weightedScore}
        />
        <LayerScoresBlock
          layerScores={recomputeLayerScores(
            result?.report?.layer_scores,
            overrides
          )}
        />
        <RuleResultsContent
          rule_results={rule_results}
          webCompanySummary={result?.report?.web_company_summary ?? null}
          overrides={overrides}
          editingField={editingField}
          draft={draft}
          isSaving={isSaving}
          unsavedFields={unsavedFields}
          fileId={fileId}
          folderId={folderId}
          onStartEdit={handleStartEdit}
          onCancelEdit={handleCancelEdit}
          onSaveDraft={handleSaveDraft}
          onSaveAll={handleSaveAll}
          onDraftChange={setDraft}
        />
      </DialogContent>
    </Dialog>
  );
};

// ─── Drawer / Side-panel variant ──────────────────────────────────────────────

const TriageResultDrawer = ({
  result,
  fileName,
  fileId,
  folderId,
  open,
  onClose,
  item,
  onArchive,
  onOpenRuleset,
  onShowEmail,
  onMarkReviewed,
  onRerunTriage,
  onAddFiles,
  onDetachFile,
  onDownloadFile,
  ruleset,
}) => {
  const {
    overrides,
    unsavedFields,
    editingField,
    draft,
    isSaving,
    handleStartEdit,
    handleCancelEdit,
    handleSaveDraft,
    handleSaveAll,
    setDraft,
    verdictOverride,
    handleSetVerdict,
  } = useTriageOverrides(result, fileId, folderId);

  const [isRerunning, setIsRerunning] = useState(false);

  const handleClose = () => {
    setDraft && setDraft(null);
    onClose();
  };

  const handleRerunTriage = async () => {
    if (!onRerunTriage) return;
    setIsRerunning(true);
    try {
      await onRerunTriage();
    } finally {
      setIsRerunning(false);
    }
  };

  const rule_results = result?.report?.rule_results ?? null;
  const { passed: effectivePassed, total: total_count } = effectiveCounts(
    result?.report,
    overrides
  );
  const activeThreshold =
    ruleset?.in_scope_threshold ?? result?.report?.in_scope_threshold ?? null;
  const derivedRaw = isReportPassing(
    result?.report,
    overrides,
    activeThreshold
  );
  const derivedPassing =
    derivedRaw === null
      ? total_count > 0 && effectivePassed === total_count
      : derivedRaw;
  const passing = isReportPassing(
    result?.report,
    overrides,
    activeThreshold,
    verdictOverride
  );
  const isPassing =
    passing === null
      ? total_count > 0 && effectivePassed === total_count
      : passing;
  const scorePercent =
    total_count > 0 ? Math.round((effectivePassed / total_count) * 100) : 0;
  const weightedScore = computeEffectiveWeightedScore(
    result?.report,
    overrides
  );
  // The bar sits under the weighted score, so it tracks that number. It fell back to the
  // rule fraction, which made a 27.3% item show a half-full bar.
  const barPercent =
    typeof weightedScore === "number"
      ? Math.round(weightedScore)
      : scorePercent;

  const metaCells = item
    ? [
        {
          label: "Broker",
          value: item.ingestion_fields?.broker?.value ?? "—",
        },
        {
          label: "Line",
          value:
            item.ingestion_fields?.line_of_business?.value ??
            item.ingestion_fields?.industryClass?.value ??
            "—",
        },
        {
          label: "Inception",
          value: item.ingestion_fields?.inception?.value ?? "—",
          mono: true,
        },
        {
          label: "Ruleset",
          value: item.triage_ruleset_name ?? "—",
        },
      ]
    : [];

  const hasEmail = item?.source === "email" && item?.email_body_file_id;
  const isReviewed = item?.triage_reviewed ?? result?.triage_reviewed ?? false;
  // What each scored rule REQUIRES, for the hover on a breakdown row. The report records
  // outcomes only, so the rule text comes from the ruleset the page resolved.
  const requirements = useMemo(() => ruleRequirementsByName(ruleset), [
    ruleset,
  ]);
  const isStale = item?.triage_stale ?? result?.triage_stale ?? false;
  // Documents attached since this report ran. The server's stale flag and this count can
  // legitimately differ — an item is stale the moment files are added, before the count can
  // be known, and a run that attempted every file leaves the flag clear while a document it
  // could not download stays unread — so the two are shown for what each actually says.
  const unreadCount = unreadDocumentCount(item);
  const removedDocs = documentsRemovedSinceReport(item, result?.report);
  const documentsChanged = unreadCount > 0 || removedDocs.length > 0;

  return (
    <Drawer
      anchor={"right"}
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            width: 620,
            maxWidth: "calc(100vw - 40px)",
            display: "flex",
            flexDirection: "column",
            boxShadow: "-16px 0 48px rgba(12,10,9,0.08)",
          },
        },
      }}
    >
      {/* ── Panel head ──────────────────────────────────────────────── */}
      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          p: "18px 22px 14px",
          position: "relative",
          flexShrink: 0,
        }}
      >
        {/* Eyebrow */}
        <Stack direction={"row"} alignItems={"center"} spacing={0.5} mb={0.75}>
          <TriageSparkleIcon
            sx={{ fontSize: "0.65rem", color: "warning.main" }}
          />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "text.disabled",
            }}
          >
            {"Triage Report"}
          </Typography>
          <Typography sx={{ color: "text.disabled", mx: 0.25, lineHeight: 1 }}>
            {"·"}
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              fontFamily: "monospace",
              color: "text.disabled",
              letterSpacing: 0,
              fontWeight: 400,
            }}
          >
            {(fileId ?? folderId ?? "—").slice(0, 8).toUpperCase()}
          </Typography>
        </Stack>

        {/* Title */}
        <Typography
          sx={{
            fontSize: 17,
            fontWeight: 600,
            letterSpacing: "-0.015em",
            lineHeight: 1.25,
            pr: 8,
            mb: 0.4,
          }}
        >
          {fileName}
        </Typography>

        {/* Sub — insured · ruleset in monospace */}
        {item && (
          <Typography
            sx={{
              fontSize: 12,
              color: "text.secondary",
              fontFamily: "monospace",
            }}
            noWrap
          >
            {[item.ingestion_fields?.insured?.value, item.triage_ruleset_name]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        )}

        {/* Action buttons — top right */}
        <Stack
          direction={"row"}
          spacing={0.25}
          sx={{ position: "absolute", top: 14, right: 16 }}
        >
          {hasEmail && onShowEmail && (
            <Tooltip title={"View original email"} arrow>
              <IconButton
                size={"small"}
                onClick={onShowEmail}
                sx={{ color: "text.secondary" }}
              >
                <EmailIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            size={"small"}
            onClick={handleClose}
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon fontSize={"small"} />
          </IconButton>
        </Stack>
      </Box>

      {/* ── Meta grid (Broker · Line · Inception · Ruleset) ────────── */}
      {metaCells.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${metaCells.length}, 1fr)`,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          {metaCells.map(({ label, value, mono }, i) => (
            <Box
              key={label}
              sx={{
                p: "10px 14px",
                ...(i < metaCells.length - 1 && {
                  borderRight: "1px solid",
                  borderColor: "divider",
                }),
              }}
            >
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "text.disabled",
                  mb: 0.5,
                  display: "block",
                }}
              >
                {label}
              </Typography>
              <Typography
                noWrap
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  ...(mono && { fontFamily: "monospace", fontSize: 12.5 }),
                }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* ── Summary block — serif fraction, neutral color ───────────── */}
      {rule_results && (
        <Box
          sx={{
            p: "18px 22px",
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            mb={1.25}
          >
            {/* The weighted score leads: it is the figure that reflects how the ruleset
                actually weighs its rules, and the pass fraction counts every entry alike.
                A ruleset with even weights withholds the weighted figure, so the fraction
                takes the lead there instead. */}
            <Stack direction={"row"} alignItems={"baseline"} spacing={1}>
              {weightedScore != null ? (
                <>
                  <Typography
                    aria-label={"Weighted score"}
                    sx={{
                      fontSize: 34,
                      lineHeight: 1,
                      fontWeight: 400,
                      letterSpacing: "-0.02em",
                      color: "text.primary",
                    }}
                  >
                    {`${weightedScore}%`}
                  </Typography>
                  <Typography
                    variant={"body2"}
                    color={"text.secondary"}
                    ml={0.5}
                  >
                    {"weighted score"}
                  </Typography>
                  <Typography
                    variant={"body2"}
                    color={"text.secondary"}
                    ml={0.5}
                  >
                    {`· ${effectivePassed}/${total_count} rules passed`}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    sx={{
                      fontSize: 34,
                      lineHeight: 1,
                      fontWeight: 400,
                      letterSpacing: "-0.02em",
                      color: "text.primary",
                    }}
                  >
                    {effectivePassed}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 24,
                      color: "text.disabled",
                      fontWeight: 400,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {`/${total_count}`}
                  </Typography>
                  <Typography
                    variant={"body2"}
                    color={"text.secondary"}
                    ml={0.5}
                  >
                    {"rules passed"}
                  </Typography>
                </>
              )}
            </Stack>

            {/* Status pill (+ stale marker when files were added after the run) */}
            <Stack direction={"row"} spacing={0.75} alignItems={"center"}>
              {(isStale || documentsChanged) && (
                <Tooltip
                  title={
                    removedDocs.length > 0
                      ? removedDocumentsHint(removedDocs.length)
                      : unreadCount > 0
                      ? NEW_DOCUMENTS_HINT
                      : STALE_REPORT_HINT
                  }
                  arrow
                >
                  <Box
                    sx={(theme) => ({
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "5px",
                      height: 20,
                      px: "8px",
                      borderRadius: "10px",
                      fontSize: 11.5,
                      fontWeight: 500,
                      bgcolor: alpha(theme.palette.warning.main, 0.14),
                      color: "warning.dark",
                    })}
                  >
                    {removedDocs.length > 0 && unreadCount > 0
                      ? "Documents changed"
                      : removedDocs.length > 0
                      ? `${removedDocs.length} ${
                          removedDocs.length === 1 ? "document" : "documents"
                        } removed`
                      : unreadCount > 0
                      ? `${unreadCount} new ${
                          unreadCount === 1 ? "document" : "documents"
                        }`
                      : "Out of date"}
                  </Box>
                </Tooltip>
              )}
              <VerdictBadge
                isPassing={isPassing}
                derivedInScope={derivedPassing}
                verdictOverride={verdictOverride}
                onSetVerdict={(value) =>
                  handleSetVerdict(value, derivedPassing, {
                    weightedScore,
                    threshold: activeThreshold,
                  })
                }
              />
            </Stack>
          </Stack>

          {/* Progress bar */}
          <Box
            sx={(theme) => ({
              height: 3,
              borderRadius: 2,
              bgcolor: theme.palette.divider,
              overflow: "hidden",
            })}
          >
            <Box
              sx={{
                height: "100%",
                borderRadius: 2,
                bgcolor: isPassing ? "success.main" : "text.disabled",
                width: `${barPercent}%`,
                transition: "width 200ms",
              }}
            />
          </Box>
        </Box>
      )}

      {/* ── Scrollable body ─────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflow: "auto", p: "16px 22px 28px" }}>
        {!result && (
          <Box sx={{ pt: 4 }}>
            <LinearProgress />
          </Box>
        )}

        {result && !rule_results && (
          <Box sx={{ pt: 4 }}>
            <LinearProgress />
          </Box>
        )}

        {rule_results && (
          <>
            <DocumentsBlock
              item={item}
              onDetachFile={onDetachFile}
              onAddFiles={onAddFiles}
              onDownloadFile={onDownloadFile}
            />
            <IncrementalRunNote report={result?.report} />
            <ScoreBreakdownBlock
              report={result?.report}
              overrides={overrides}
              score={weightedScore}
              requirements={requirements}
            />
            <LayerScoresBlock
              layerScores={recomputeLayerScores(
                result?.report?.layer_scores,
                overrides
              )}
              requirements={requirements}
            />

            {/* Section label + edit ruleset button */}
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              mb={1.5}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "text.disabled",
                }}
              >
                {`Rule evaluation · ${rule_results.length} rule${
                  rule_results.length !== 1 ? "s" : ""
                }`}
              </Typography>
              {onOpenRuleset && (
                <MuiButton
                  size={"small"}
                  variant={"text"}
                  startIcon={<EditIcon sx={{ fontSize: "0.8rem" }} />}
                  onClick={onOpenRuleset}
                  sx={{
                    fontSize: "0.72rem",
                    textTransform: "none",
                    color: "text.secondary",
                    "&:hover": {
                      color: "text.primary",
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  {"Edit ruleset"}
                </MuiButton>
              )}
            </Stack>

            <RuleResultsContent
              rule_results={rule_results}
              webCompanySummary={result?.report?.web_company_summary ?? null}
              overrides={overrides}
              editingField={editingField}
              draft={draft}
              isSaving={isSaving}
              unsavedFields={unsavedFields}
              fileId={fileId}
              folderId={folderId}
              onStartEdit={handleStartEdit}
              onCancelEdit={handleCancelEdit}
              onSaveDraft={handleSaveDraft}
              onSaveAll={handleSaveAll}
              onDraftChange={setDraft}
              emailBodyFileId={item?.email_body_file_id ?? null}
              onOpenEmail={onShowEmail}
              attachmentFiles={item?.attachment_files ?? []}
            />

            {/* AI grounding info card */}
            <Box
              sx={(theme) => ({
                mt: 2,
                p: "12px 14px",
                bgcolor: alpha(theme.palette.text.primary, 0.02),
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
              })}
            >
              <Typography
                sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.5 }}
              >
                <Box component={"strong"} sx={{ color: "text.primary" }}>
                  {"AI reasoning"}
                </Box>
                {
                  " is grounded in the submission email body and attached documents. Click any source to jump to the exact page. Rules can be overridden manually — overrides are logged to the audit trail."
                }
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {/* ── Action footer ───────────────────────────────────────────── */}
      <Box
        sx={{
          p: "12px 22px",
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "background.paper",
          flexShrink: 0,
        }}
      >
        {/* Left: Re-run triage + Add files */}
        <Stack direction={"row"} spacing={1}>
          {/* One re-run button, deliberately. The deployment's configured mode governs it,
              and a second button forcing a full re-read was indistinguishable from this one
              wherever that mode is already full — which is every deployment today. The
              per-request override still exists on the endpoint for API callers. */}
          <Tooltip
            title={
              removedDocs.length > 0
                ? removedDocumentsHint(removedDocs.length)
                : unreadCount > 0
                ? NEW_DOCUMENTS_HINT
                : "Re-evaluate this submission against the current ruleset"
            }
            arrow
          >
            {/* The span is load-bearing: components/common/Button is a plain function
                component with no forwardRef, and MUI Tooltip needs a child that can hold a
                ref. It also keeps the tooltip working while the button is disabled
                mid-run. */}
            <span>
              <Button
                size={"small"}
                // Amber, not the primary green: the stale pill and the unread document
                // icons already speak amber for "needs attention", and green here would
                // sit beside Mark reviewed — a confirm action — saying something different.
                color={documentsChanged || isStale ? "warning" : "secondary"}
                startIcon={
                  <ReplayIcon sx={{ fontSize: "0.85rem !important" }} />
                }
                sx={{ fontSize: "0.78rem", textTransform: "none" }}
                onClick={() => handleRerunTriage()}
                disabled={!onRerunTriage || isRerunning}
              >
                {isRerunning
                  ? "Re-running…"
                  : unreadCount > 0
                  ? `Re-run triage · ${unreadCount} new`
                  : removedDocs.length > 0
                  ? "Re-run triage · recommended"
                  : "Re-run triage"}
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {/* Right: Archive + Mark reviewed */}
        <Stack direction={"row"} spacing={1}>
          {onArchive && item && (
            <Button
              size={"small"}
              color={"secondary"}
              startIcon={
                <ArchiveIcon sx={{ fontSize: "0.85rem !important" }} />
              }
              onClick={() => {
                onArchive(item, !item.triage_archived);
                handleClose();
              }}
              sx={{ fontSize: "0.78rem", textTransform: "none" }}
            >
              {item.triage_archived ? "Unarchive" : "Archive"}
            </Button>
          )}
          {isReviewed ? (
            <MuiButton
              size={"small"}
              variant={"outlined"}
              startIcon={
                <ReviewedIcon sx={{ fontSize: "0.85rem !important" }} />
              }
              onClick={() =>
                onMarkReviewed && item && onMarkReviewed(item, false)
              }
              sx={{
                fontSize: "0.78rem",
                textTransform: "none",
                color: "success.dark",
                borderColor: "success.main",
                bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
                "&:hover": {
                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.04),
                  borderColor: "success.dark",
                  color: "text.secondary",
                },
              }}
            >
              {"Reviewed"}
            </MuiButton>
          ) : (
            <Button
              size={"small"}
              startIcon={
                <ReviewedIcon sx={{ fontSize: "0.85rem !important" }} />
              }
              onClick={() =>
                onMarkReviewed && item && onMarkReviewed(item, true)
              }
              sx={{ fontSize: "0.78rem", textTransform: "none" }}
            >
              {"Mark reviewed"}
            </Button>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
};

// ─── Public component ─────────────────────────────────────────────────────────

const TriageResultDialog = ({
  result,
  fileName,
  fileId,
  folderId,
  open,
  onClose,
  useDrawer = false,
  item = null,
  onArchive = null,
  onOpenRuleset = null,
  onShowEmail = null,
  onMarkReviewed = null,
  onRerunTriage = null,
  onAddFiles = null,
  onDetachFile = null,
  onDownloadFile = null,
  ruleset = null,
}) => {
  if (useDrawer) {
    return (
      <TriageResultDrawer
        result={result}
        fileName={fileName}
        fileId={fileId}
        folderId={folderId}
        open={open}
        onClose={onClose}
        item={item}
        onArchive={onArchive}
        onOpenRuleset={onOpenRuleset}
        onShowEmail={onShowEmail}
        onMarkReviewed={onMarkReviewed}
        onRerunTriage={onRerunTriage}
        onAddFiles={onAddFiles}
        onDetachFile={onDetachFile}
        onDownloadFile={onDownloadFile}
        ruleset={ruleset}
      />
    );
  }
  return (
    <TriageResultDialogVariant
      result={result}
      fileName={fileName}
      fileId={fileId}
      folderId={folderId}
      open={open}
      onClose={onClose}
    />
  );
};

export { OUTCOME_META };
export default TriageResultDialog;
